import {
  CoxAnalyticsConfig,
  CoxAnalyticsDataLayer,
  WindowWithCoxAnalytics
} from "@coxauto-ui/core-models-analytics-cox-analytics";

let config: CoxAnalyticsConfig | null = null;

/**
 * Wait for coxAutoDataLayer to be available
 */
const waitForDataLayer = async (
  maxAttempts = 50
): Promise<CoxAnalyticsDataLayer> => {
  return new Promise(resolve => {
    let attempts = 0;

    // Check if we can access window.top safely
    let topWindow: WindowWithCoxAnalytics | null = null;
    try {
      topWindow = window.top as WindowWithCoxAnalytics;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(
        "Cannot access window.top, using current window for Cox Analytics data layer"
      );
      topWindow = window as WindowWithCoxAnalytics;
    }

    const checkForDataLayer = () => {
      attempts++;

      if (
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        topWindow!.coxAutoDataLayer &&
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        typeof topWindow!.coxAutoDataLayer?.push === "function"
      ) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        resolve(topWindow!.coxAutoDataLayer!);
        return;
      }

      if (attempts >= maxAttempts) {
        // eslint-disable-next-line no-console
        console.warn(
          `Cox Analytics data layer not available after ${maxAttempts} attempts - analytics will be disabled`
        );

        // Return a mock data layer that silently ignores pushes
        resolve({
          push: () => {
            // Silent no-op
          },
          length: 0
        } as CoxAnalyticsDataLayer);
        return;
      }

      // Aggressive polling with varying delays
      let delay;
      if (attempts <= 10) {
        delay = 25; // Very fast polling for first 250ms
      } else if (attempts <= 20) {
        delay = 50; // Then every 50ms for next 500ms
      } else if (attempts <= 30) {
        delay = 100; // Then every 100ms for next 1000ms
      } else {
        delay = 200; // Then slower polling
      }

      setTimeout(checkForDataLayer, delay);
    };

    checkForDataLayer();
  });
};

export const CoxAnalyticsManager = {
  /**
   * Initialize Cox Analytics with the provided configuration
   * This should be called early in the application lifecycle (e.g., in main.tsx)
   * This will only initialize if the LaunchDarkly feature flag 'cxm.cdl.analytics.enable' is true.
   *
   * @param analyticsConfig - Configuration for Cox Analytics
   * @param enableAnalytics - LaunchDarkly feature flag value for 'cxm.cdl.analytics.enable'.
   *                          Pass the flag value from useCxmFlags() hook.
   */
  initialize(
    analyticsConfig: CoxAnalyticsConfig,
    enableAnalytics: boolean
  ): void {
    // Only initialize if the LaunchDarkly toggle is explicitly true
    if (enableAnalytics === true) {
      config = analyticsConfig;

      const scriptSrc = config.isProduction
        ? "https://assets.prod.analytics.dealer.com/pixall/pix-retail.min.js"
        : "https://assets.non-prod.analytics.dealer.com/pixall/pix-retail.min.js";

      const script = document.createElement("script");
      script.src = scriptSrc;
      script.defer = true;
      script.setAttribute("data-testid", "cox-analytics-script");

      document.head.appendChild(script);
    }
  },

  /**
   * Push data to the analytics layer
   * This method will only work if Cox Analytics was initialized with the LaunchDarkly toggle enabled.
   *
   * @param data - Analytics data to push to the Cox Analytics data layer
   */
  async push(data: Record<string, unknown>): Promise<void> {
    if (!config) {
      // eslint-disable-next-line no-console
      console.warn("Cox Analytics not initialized.");
      return;
    }

    try {
      await waitForDataLayer();

      (window.top as WindowWithCoxAnalytics).coxAutoDataLayer?.push(
        data as Record<string, unknown>
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("Failed to push Cox Analytics data:", error);
    }
  },

  /**
   * Check if Cox Analytics has been initialized with LaunchDarkly feature flag enabled
   *
   * @returns true if Cox Analytics was initialized with the LaunchDarkly toggle enabled
   */
  isInitialized(): boolean {
    return config !== null;
  },

  /**
   * Get the current configuration
   */
  getConfig(): CoxAnalyticsConfig | null {
    return config;
  },

  /**
   * Reset the analytics manager (primarily for testing)
   */
  reset(): void {
    config = null;
  }
};
